import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { toast } from "react-toastify";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import "./sops.css";

const EMPTY_FORM = {
  sop_for: "",
  title: "",
  content: "",
};

const BRANCH_OPTIONS = [
  { id: "15", name: "F-11" },
  { id: "1", name: "G-13" },
];

const extractRows = (response) => {
  const payload = response?.data?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;

  return [];
};

const renderHtml = (content) => ({ __html: content || "<p>No SOP content added yet.</p>" });

export default function SOPManager() {
  const { state } = useContext(NoteState);
  const { branch_id, role } = state;
  const canSeeAllBranches = String(role) === "1" || String(role) === "12";
  const [branchFilter, setBranchFilter] = useState(canSeeAllBranches ? "all" : String(branch_id || ""));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("2");
  const [sops, setSops] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const selectedSop = useMemo(
    () => sops.find((row) => String(row.id) === String(selectedId)) || null,
    [selectedId, sops]
  );

  const loadSops = useCallback(async () => {
    const requestedBranch = canSeeAllBranches
      ? branchFilter === "all"
        ? ""
        : branchFilter
      : branch_id;

    if (!canSeeAllBranches && !branch_id) {
      setSops([]);
      setSelectedId(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await UserServices.getSops({
        branch_id: requestedBranch,
        search,
        status: statusFilter,
        limit: 100,
        page: 1,
      });
      const rows = extractRows(response);
      setSops(rows);
      setSelectedId((current) =>
        rows.find((row) => String(row.id) === String(current)) ? current : rows[0]?.id || null
      );
    } catch (error) {
      if (error?.response?.status === 404) {
        setSops([]);
        setSelectedId(null);
      } else {
        toast.error(error?.response?.data?.message || "Unable to load SOPs.");
      }
    } finally {
      setLoading(false);
    }
  }, [branchFilter, branch_id, canSeeAllBranches, search, statusFilter]);

  useEffect(() => {
    loadSops();
  }, [loadSops]);

  const resetForm = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
  };

  const beginEdit = (row) => {
    setEditId(row.id);
    setSelectedId(row.id);
    setForm({
      sop_for: row.sop_for || "",
      title: row.title || "",
      content: row.content || "",
      branch_id: String(row.branch_id || branch_id || ""),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = async (event) => {
    event.preventDefault();

    const saveBranch = canSeeAllBranches ? form.branch_id || branch_id : branch_id;
    if (!saveBranch) {
      toast.error("Choose the branch this SOP belongs to.");
      return;
    }

    if (!form.sop_for.trim() || !form.title.trim() || !form.content.trim()) {
      toast.error("Please complete SOP For, title, and SOP content.");
      return;
    }

    setSaving(true);

    try {
      if (editId) {
        await UserServices.updateSop(editId, {
          ...form,
          branch_id: saveBranch,
        });
        toast.success("SOP updated successfully.");
      } else {
        await UserServices.addSop({
          ...form,
          branch_id: saveBranch,
        });
        toast.success("SOP created successfully.");
      }

      resetForm();
      await loadSops();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to save SOP.");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, action, message) => {
    try {
      await UserServices.sopActions(id, action);
      toast.success(message);
      if (String(selectedId) === String(id) && action === "2") {
        setSelectedId(null);
      }
      await loadSops();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to update SOP status.");
    }
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="sop-shell">
            <section className="sop-hero">
              <div>
                <span className="sop-eyebrow">SOPs</span>
                <h2>SOP Manager</h2>
                <p>
                  Create clean SOP documents for trainers with rich formatting, tables, lists,
                  and clear presentation.
                </p>
              </div>
              <div className="sop-hero-chip">
                {String(role) === "12" ? "HR workspace" : "SOP workspace"}
              </div>
            </section>

            <section className="sop-grid">
              <div className="sop-card">
                <div className="sop-card-header">
                  <div>
                    <span className="sop-section-label">Editor</span>
                    <h4>{editId ? "Update SOP" : "Create SOP"}</h4>
                  </div>
                </div>

                <form className="sop-form" onSubmit={handleSave}>
                  {canSeeAllBranches ? (
                    <select
                      className="form-select"
                      value={form.branch_id || String(branch_id || "")}
                      onChange={(event) => setForm({ ...form, branch_id: event.target.value })}
                    >
                      {BRANCH_OPTIONS.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  <input
                    className="form-control"
                    placeholder="SOP For e.g. Personal Trainers / Front Desk / GX Trainers"
                    value={form.sop_for}
                    onChange={(event) => setForm({ ...form, sop_for: event.target.value })}
                  />
                  <input
                    className="form-control"
                    placeholder="SOP Title"
                    value={form.title}
                    onChange={(event) => setForm({ ...form, title: event.target.value })}
                  />
                  <div className="sop-editor-wrap">
                    <CKEditor
                      key={editId || "new-sop"}
                      editor={ClassicEditor}
                      data={form.content}
                      onChange={(_, editor) =>
                        setForm((current) => ({ ...current, content: editor.getData() }))
                      }
                    />
                  </div>
                  <div className="sop-form-actions">
                    <button className="btn btn-primary" disabled={saving}>
                      {saving ? "Saving..." : editId ? "Update SOP" : "Save SOP"}
                    </button>
                    {editId ? (
                      <button type="button" className="btn btn-light" onClick={resetForm}>
                        Cancel Edit
                      </button>
                    ) : null}
                  </div>
                </form>
              </div>

              <div className="sop-card">
                <div className="sop-card-header">
                  <div>
                    <span className="sop-section-label">Preview</span>
                    <h4>{selectedSop?.title || "Live SOP Preview"}</h4>
                    <p>{selectedSop?.sop_for || "Select or create an SOP to preview it here."}</p>
                  </div>
                </div>
                <div className="sop-content-preview" dangerouslySetInnerHTML={renderHtml(selectedSop?.content)} />
              </div>
            </section>

            <section className="sop-card">
              <div className="sop-card-header sop-card-header-spread">
                <div>
                  <span className="sop-section-label">Library</span>
                  <h4>Saved SOPs</h4>
                </div>
                <div className="sop-toolbar">
                  {canSeeAllBranches ? (
                    <select
                      className="form-select"
                      value={branchFilter}
                      onChange={(event) => setBranchFilter(event.target.value)}
                    >
                      <option value="all">All branches</option>
                      {BRANCH_OPTIONS.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  <input
                    className="form-control"
                    placeholder="Search SOPs"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                  >
                    <option value="2">All Active + Inactive</option>
                    <option value="1">Active Only</option>
                    <option value="0">Inactive Only</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="sop-empty">Loading SOPs...</div>
              ) : sops.length ? (
                <div className="sop-list">
                  {sops.map((row) => (
                    <div
                      key={row.id}
                      className={`sop-list-item ${String(selectedId) === String(row.id) ? "active" : ""}`}
                    >
                      <button
                        type="button"
                        className="sop-list-select"
                        onClick={() => setSelectedId(row.id)}
                      >
                        <div className="sop-list-meta">
                          <strong>{row.title}</strong>
                          <span>{row.sop_for}</span>
                          <small>
                            {String(row.status) === "1" ? "Active" : "Inactive"} | Branch:{" "}
                            {row.branch?.name || "N/A"}
                          </small>
                        </div>
                      </button>
                      <div className="sop-list-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => beginEdit(row)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() =>
                            updateStatus(
                              row.id,
                              String(row.status) === "1" ? "0" : "1",
                              `SOP ${String(row.status) === "1" ? "inactivated" : "activated"} successfully.`
                            )
                          }
                        >
                          {String(row.status) === "1" ? "Inactive" : "Activate"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => updateStatus(row.id, "2", "SOP deleted successfully.")}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="sop-empty">No SOPs found for this branch yet.</div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
